import React, { useState, useEffect, useContext } from 'react';
import { TextField, Button, Grid, Typography, Paper, Box } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/system';
import { ContactContext } from '../ContactContext';
import MyWebsite from '../../utils/MyWebsite';
import { showSwalNotification } from '../../utils/SwalResponse';
import { uploadMedia } from '../../utils/MediaUploader';
import LoadingComponent from '../../utils/LoadingComponent';
import { AuthContext } from '../../auth/AuthContext';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 600,
  margin: 'auto',
  background: 'linear-gradient(135deg,rgb(255, 255, 255,0.85) 0%,rgb(208, 255, 179,0.7) 100%)',
  boxShadow: '0 8px 20px rgba(55, 129, 9, 0.48)',
  borderRadius: '5px',
}));

const SubmitButton = styled(Button)({
  backgroundColor: '#2e7d32',
  color: '#fff',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#1b5e20',
  },
});

const ContactForm = () => {
  const { submitContactForm } = useContext(ContactContext);
  const {currentUser} = useContext(AuthContext)
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    subject: '',
    message: '',
    website: '',
    image: null,
  });

  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem('contactForm'));
    if (savedData) {
      setFormData(savedData);
    }
  }, []);

  useEffect(() => {
    const { image, ...toSave } = formData;
    localStorage.setItem('contactForm', JSON.stringify(toSave));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true)
    if (formData?.website) return ;

    if (formData?.image instanceof File) {
      const uploadedImage = await uploadMedia(formData.image);
      formData.image = uploadedImage?.url;
    }
    // console.log('Form Data Submitted:', formData);
    const response = await submitContactForm(formData);
    showSwalNotification(response)
    if (response?.status === 'success') {
      setFormData({
        name: currentUser?.name ||'',
        email: currentUser?.email ||'',
        subject: '',
        message: '',
        website: '',
        image: null,
      });
      localStorage.removeItem('contactForm');
    }
    setLoading(false);
  };
  if (!!loading) return <LoadingComponent loadingText={"Submitting Contact Form ..."}/>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
    >
      <FormContainer>
        <Typography variant="h5" gutterBottom sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
          Contact Us
        </Typography>
        <Box component="form" onSubmit={handleSubmit} encType="multipart/form-data">
          <Grid container spacing={2}>
            <MyWebsite onChange={handleChange} /> 

            {/* Name and Email side by side */}
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Subject in its own row */}
            {/* <Grid xs={12} sm={12}> */}
              <TextField
                name="subject"
                label="Subject"
                fullWidth
                value={formData.subject}
                onChange={handleChange}
                required
              />
            {/* </Grid> */}

            {/* Message in its own row */}
            {/* <Grid item xs={12}> */}
              <TextField
                name="message"
                label="Message"
                fullWidth
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
              />
            {/* </Grid> */}

            {/* Image upload in its own row */}
            {/* <Grid item xs={12}> */}
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ borderColor: '#2e7d32', color: '#2e7d32' }}
              >
                Attatch Image (Optional)
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  hidden
                  onChange={handleChange}
                />
              </Button>
              {formData.image && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Selected: {formData.image.name}
                </Typography>
              )}
            {/* </Grid> */}

            {/* Submit Button */}
            <Grid item xs={12}>
              <SubmitButton type="submit" fullWidth variant="contained">
                Submit
              </SubmitButton>
            </Grid>
          </Grid>
        </Box>
      </FormContainer>
    </motion.div>
  );
};

export default ContactForm;
